import React, { useState } from 'react';
import { Menu, MenuButton, MenuList, MenuItem, IconButton, Box, Flex } from '@chakra-ui/react';
import { faEllipsisH, faCalendar, faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from 'shared-components';
import { IApplicantOfferRequirementTask } from 'types';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import { getCalendlySchedulingInfo } from '@/store/apply/actions';
import { BookInterviewModal } from './modals/BookInterviewModal';
import { InterviewDetailsModal } from './modals/InterviewDetailsModal';

const CALENDLY_INTERVIEW_PREFIX = 'CalendlyInterview#';

interface CalendlyInterviewMenuProps {
  requirement: IApplicantOfferRequirementTask;
  uni: string;
  offerId: string;
  applicantId: string;
  isBooked: boolean;
  isOfferIssued: boolean;
  utmKey: string;
  startDate?: number;
  joinUrl?: string;
  onUpload?: () => void;
}

const CalendlyInterviewMenu: React.FC<CalendlyInterviewMenuProps> = ({
  requirement,
  uni,
  offerId,
  applicantId,
  isBooked,
  isOfferIssued,
  utmKey,
  startDate,
  joinUrl,
  onUpload,
}) => {
  const dispatch = useDispatch();
  const [showBookModal, setShowBookModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const eventType = requirement.id.startsWith(CALENDLY_INTERVIEW_PREFIX)
    ? requirement.id.substring(CALENDLY_INTERVIEW_PREFIX.length)
    : '';
  const schedulingData = useSelector((state: ApplicationState) => state.apply.calendlySchedulingInfos[eventType]);
  const schedulingUrl = schedulingData?.data?.schedulingUrl ?? null;
  const interviewName = schedulingData?.data?.name ?? null;
  const description = schedulingData?.data?.description ?? null;

  const handleMenuOpen = (): void => {
    dispatch(getCalendlySchedulingInfo({ uni, eventType }));
  };

  const canOpenBooking = isBooked;
  const canBookInterview = !isBooked && isOfferIssued;

  if (!requirement.id.startsWith(CALENDLY_INTERVIEW_PREFIX) || (!canOpenBooking && !canBookInterview && !onUpload)) {
    return null;
  }

  return (
    <>
      <BookInterviewModal
        isOpen={showBookModal}
        onClose={() => setShowBookModal(false)}
        applicantId={applicantId}
        uni={uni}
        offerId={offerId}
        utmKey={utmKey}
        schedulingUrl={schedulingUrl}
      />
      <InterviewDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        interviewName={interviewName}
        description={description}
        startDate={startDate}
        joinUrl={joinUrl}
      />
      <Box my={-1}>
        <Menu placement="bottom-end" onOpen={handleMenuOpen}>
          <MenuButton
            as={IconButton}
            icon={<Icon icon={faEllipsisH} />}
            size="sm"
            variant="secondary"
            colorScheme="gray"
            aria-label="More actions"
          />
          <MenuList>
            {canOpenBooking && (
              <MenuItem onClick={() => setShowDetailsModal(true)}>
                <Flex align="center">
                  <Icon icon={faCalendar} mr={2} />
                  Open booking
                </Flex>
              </MenuItem>
            )}
            {canBookInterview && (
              <MenuItem onClick={() => setShowBookModal(true)}>
                <Flex align="center">
                  <Icon icon={faCalendar} mr={2} />
                  Book interview
                </Flex>
              </MenuItem>
            )}
            {onUpload && (
              <MenuItem onClick={onUpload}>
                <Flex align="center">
                  <Icon icon={faCloudArrowUp} mr={2} />
                  Upload file
                </Flex>
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Box>
    </>
  );
};

export default CalendlyInterviewMenu;
